import { createPortal } from 'react-dom';
import { Toast, ToastPosition } from './ui/Toast';
import {
  useNotifications,
  useNotificationPosition,
  useNotificationsStore,
} from '../stores/notifications.store';

// Position styles for the notification container
const positionStyles: Record<ToastPosition, string> = {
  'top-right': 'top-4 right-4 items-end',
  'top-left': 'top-4 left-4 items-start',
  'top-center': 'top-4 left-1/2 -translate-x-1/2 items-center',
  'bottom-right': 'bottom-4 right-4 items-end',
  'bottom-left': 'bottom-4 left-4 items-start',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2 items-center',
};

// Animation direction based on position
const getAnimationClass = (position: ToastPosition, isEntering: boolean): string => {
  const isTop = position.startsWith('top');
  const isRight = position.endsWith('right');
  const isCenter = position.endsWith('center');

  if (isEntering) {
    if (isCenter) return isTop ? 'animate-slide-down' : 'animate-slide-up';
    return isRight ? 'animate-slide-left' : 'animate-slide-right';
  }

  return '';
};

export interface NotificationCenterProps {
  /** Override the position from the store */
  position?: ToastPosition;
  /** Additional CSS classes for the container */
  className?: string;
}

export const NotificationCenter = ({
  position: positionOverride,
  className = '',
}: NotificationCenterProps) => {
  const notifications = useNotifications();
  const storePosition = useNotificationPosition();
  const removeNotification = useNotificationsStore((state) => state.removeNotification);

  const position = positionOverride ?? storePosition;
  const isBottomPosition = position.startsWith('bottom');

  // If no notifications, don't render anything
  if (notifications.length === 0) {
    return null;
  }

  const content = (
    <div
      className={`
        fixed z-50 flex flex-col gap-3 pointer-events-none
        w-full max-w-sm px-4 sm:px-0
        ${positionStyles[position]}
        ${className}
      `.trim()}
      role="region"
      aria-label="Notificaciones"
      aria-live="polite"
    >
      {/* Reverse order for bottom positions so newest appears at bottom */}
      {(isBottomPosition ? [...notifications].reverse() : notifications).map(
        (notification) => (
          <div
            key={notification.id}
            className={`
              pointer-events-auto
              ${getAnimationClass(position, true)}
            `}
          >
            <Toast
              id={notification.id}
              variant={notification.variant}
              title={notification.title}
              message={notification.message}
              duration={notification.duration}
              action={notification.action}
              onClose={() => removeNotification(notification.id)}
            />
          </div>
        )
      )}
    </div>
  );

  // Use portal to render at document body level
  return createPortal(content, document.body);
};

// Add custom animations to tailwind (add to index.css or tailwind config)
// These styles should be added to your CSS:
/*
@keyframes slide-down {
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slide-up {
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slide-left {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slide-right {
  from {
    opacity: 0;
    transform: translateX(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slide-down {
  animation: slide-down 0.3s ease-out;
}

.animate-slide-up {
  animation: slide-up 0.3s ease-out;
}

.animate-slide-left {
  animation: slide-left 0.3s ease-out;
}

.animate-slide-right {
  animation: slide-right 0.3s ease-out;
}
*/

export default NotificationCenter;
